import { Cookies } from '@/utils';
import {
  COALEDU_FRONT_USERINFO,
  TOKEN_KEY,
  SSO_COOKIE_NAME,
  CLIENT_ID,
  ssoURL,
  courseApis,
  LogAPIURL,
  BigDataCode,
} from './Constant';
import request from '@/bootstarp/request';
import { useUserStore } from '@/stores/user';
import { siteURL } from './Constant';
import { ElMessageBox } from 'element-plus';
import { getParams } from '@/utils';
import { stringify } from '@/utils/qs';

// 环境配置
const CONFIG = {
  isDEV: location.host.includes('localhost'),
  KEEP_ONLINE_INTERVAL: 600 * 1000, // 10分钟
  IMG_CLEANUP_DELAY: 1000, // 1秒
};

/**
 * 用户信息存储管理器
 * 统一管理用户信息的读取、存储和删除操作，并同步到 Pinia
 */
class UserStorageManager {
  /**
   * 获取用户信息
   * 优先从localStorage读取，如果为空则从Cookie中读取并同步到localStorage
   */
  static getUserInfo() {
    try {
      // 先从localStorage读取
      let userinfo = window.localStorage[COALEDU_FRONT_USERINFO];
      if (this._isEmpty(userinfo)) {
        // localStorage为空，尝试从Cookie读取
        userinfo = Cookies.get(COALEDU_FRONT_USERINFO);
        if (this._isEmpty(userinfo)) {
          return '';
        }

        // 解析Cookie中的用户信息
        userinfo = decodeURIComponent(userinfo);
        const parsedUserInfo = JSON.parse(userinfo);

        // 修正token格式
        if (parsedUserInfo.token) {
          parsedUserInfo.token = parsedUserInfo.token.replace('Bearer+', 'Bearer ');
        }

        // 同步到localStorage
        this.setUserInfo(JSON.stringify(parsedUserInfo));
        return parsedUserInfo;
      }

      return JSON.parse(userinfo);
    } catch (error) {
      console.error('获取用户信息失败:', error);
      return '';
    }
  }

  /**
   * 设置用户信息到localStorage
   */
  static setUserInfo(userinfo: string) {
    try {
      // 清除Cookie中的用户信息，避免数据不一致
      Cookies.remove(COALEDU_FRONT_USERINFO);
      window.localStorage.setItem(COALEDU_FRONT_USERINFO, userinfo);

      // 同步到 Pinia store
      this._syncToPiniaStore(userinfo);
    } catch (error) {
      console.error('设置用户信息失败:', error);
    }
  }

  /**
   * 删除用户信息
   * 同时清除localStorage、Cookie和 Pinia store 中的数据
   */
  static removeUserInfo() {
    if (CONFIG.isDEV) {
      return;
    }
    try {
      window.localStorage.removeItem(COALEDU_FRONT_USERINFO);
      Cookies.remove(COALEDU_FRONT_USERINFO);

      // 清空 Pinia store
      this._clearPiniaStore();
    } catch (error) {
      console.error('删除用户信息失败:', error);
    }
  }
  static _syncToPiniaStore(userinfo: string) {
    try {
      const userStore = useUserStore();
      const parsedUserInfo = JSON.parse(userinfo);

      if (parsedUserInfo && parsedUserInfo.userName) {
        userStore.handleLoginSuccess(parsedUserInfo, parsedUserInfo.token || '');
      }
    } catch (error) {
      console.error('同步用户信息到 Pinia store 失败:', error);
    }
  }

  static _clearPiniaStore() {
    try {
      const userStore = useUserStore();
      userStore.handleLogout();
    } catch (error) {
      console.error('清空 Pinia store 失败:', error);
    }
  }

  /**
   * 检查值是否为空
   * @private
   */
  static _isEmpty(value) {
    return value === '' || value == null || value === undefined || value === 'undefined';
  }
}

class TokenManager {
  static getToken() {
    try {
      let token = window.localStorage[TOKEN_KEY];
      if (UserStorageManager._isEmpty(token)) {
        const userinfo = UserStorageManager.getUserInfo();
        if (userinfo && userinfo.token) {
          token = userinfo.token;
          this.setToken(token);
        }
      }
      return token || '';
    } catch (error) {
      console.error('获取Token失败:', error);
      return '';
    }
  }

  static setToken(token: string) {
    window.localStorage.setItem(TOKEN_KEY, token);

    try {
      const userStore = useUserStore();
      userStore.setToken(token);
    } catch (error) {
      console.error('同步Token到 Pinia store 失败:', error);
    }
  }

  static removeToken() {
    window.localStorage.removeItem(TOKEN_KEY);
    Cookies.remove(SSO_COOKIE_NAME);

    try {
      const userStore = useUserStore();
      userStore.setToken('');
    } catch (error) {
      console.error('清空 Pinia store 中的 Token 失败:', error);
    }
  }
}

/**
 * SSO单点登录认证管理器
 * 处理用户登录状态检查、SSO登录流程和注销操作
 */
class SSOAuthManager {
  static async checkLogin() {
    try {
      const url = `${ssoURL}online`;
      let data = await request.get<{ Login: boolean }>(url, {
        params: { time: Date.now() },
      });
      const userStore = useUserStore();
      userStore.setLoginState(data.Login);
      if (data.Login) {
        await this._handleUserOnline(data);
        return true;
      } else {
        this._handleUserOffline();
      }
    } catch (error) {
      console.error('检查登录状态失败:', error);
      this._handleUserOffline();
    }
  }

  static async _handleUserOnline(data) {
    const token = TokenManager.getToken();
    if (token) {
      const userInfo = UserStorageManager.getUserInfo();
      // 验证本地用户信息与SSO用户信息是否一致
      if (userInfo && data.AccessName === userInfo.userName) {
        return;
      }
      // 用户信息不一致，需要重新登录
      this._logoutWithoutRedirect();
    }
    await this._requestSSOLogin();
  }

  static _handleUserOffline() {
    this._logout();
    console.log('用户未登录');
  }

  static async _requestSSOLogin() {
    try {
      const url = this._buildSSOLoginUrl();
      console.log('SSO登录请求URL:', url);

      const data = await (request as any).jsonp(url);
      console.log(data, 'SSO登录请求成功');
      if (data.Success) {
        await this._executeLogin(data.RedirectURL);
      } else {
        this._logout();
      }
    } catch (error: any) {
      console.error('SSO登录请求失败:', error);
      console.error('请求URL:', this._buildSSOLoginUrl());
      console.error('错误详情:', error.message);
      this._logout();
    }
  }

  static _buildSSOLoginUrl() {
    const params = new URLSearchParams({
      ContentType: 'jsonp',
      client_id: CLIENT_ID,
      response_type: 'code',
      scope: 'user_info',
      redirect_uri: `${courseApis}member/login`,
    });

    return `${ssoURL}doLogin?${params.toString()}`;
  }

  static async _executeLogin(redirectURL) {
    try {
      const data = await request.get(redirectURL);

      if (data.status === 1) {
        // 登录成功，保存用户信息和token
        TokenManager.setToken(data.data.token);
        UserStorageManager.setUserInfo(JSON.stringify(data.data));

        try {
          const userStore = useUserStore();
          userStore.handleLoginSuccess(data.data, data.data.token);
        } catch (error) {
          console.error('同步登录状态到 Pinia store 失败:', error);
        }

        console.log('登录成功');
      } else {
        console.error('登录失败:', data.message);
      }
    } catch (error) {
      console.error('执行登录失败:', error);
    }
  }

  /**
   * 执行注销操作（不跳转页面）
   * @private
   */
  static _logoutWithoutRedirect() {
    if (CONFIG.isDEV) return;

    const token = TokenManager.getToken();
    TokenManager.removeToken();
    UserStorageManager.removeUserInfo();

    try {
      const userStore = useUserStore();
      userStore.handleLogout();
    } catch (error) {
      console.error('清空 Pinia store 失败:', error);
    }

    if (!token) return;

    this._callLogoutAPI(token);
  }

  static _logout() {
    if (CONFIG.isDEV) return;

    const token = TokenManager.getToken();

    TokenManager.removeToken();
    UserStorageManager.removeUserInfo();

    try {
      const userStore = useUserStore();
      userStore.handleLogout();
    } catch (error) {
      console.error('清空 Pinia store 失败:', error);
    }

    if (!token) return;

    this._callLogoutAPI(token);
  }

  static async _callLogoutAPI(token) {
    try {
      const headers = {};
      if (CONFIG.isDEV) {
        headers[SSO_COOKIE_NAME] = token;
      }

      const data = await request.get(`${courseApis}member/weblogout`);

      if (data.status === 1) {
        console.log('注销成功');
      } else {
        console.error('注销失败:', data.message);
      }
    } catch (error) {
      console.error('注销API调用失败:', error);
    }
  }
}

/**
 * SSO页面导航管理器
 * 处理各种SSO相关的页面跳转操作
 */
class SSONavigationManager {
  /**
   * 跳转到SSO登录页面
   * @param {string} customReferer - 自定义返回地址（可选）
   */
  static async toSSOLogin(customReferer?: string) {
    if (typeof customReferer !== 'string') {
      customReferer = '';
    }
    try {
      const referer = customReferer || window.location.href;
      const res = await request.get(`${courseApis}member/login`, {
        params: { referer },
      });
      if (res.status) {
        if (res.data?.redirectURI) {
          window.location.href = res.data.redirectURI;
        } else {
          console.error('获取SSO登录重定向地址失败');
        }
      } else {
        console.error('获取SSO登录重定向地址失败');
      }
    } catch (error) {
      console.error('跳转SSO登录失败:', error);
    }
  }

  /**
   * 跳转到SSO注销页面
   * 先执行本地注销，然后跳转到SSO注销页面
   */
  static toSSOLogout() {
    // 先执行本地注销
    SSOAuthManager._logout();
    // 跳转到SSO注销页面
    const logoutUrl = `${ssoURL}ssoLogout?referer=${encodeURIComponent(siteURL || location.origin)}`;
    window.location.href = logoutUrl;
  }

  static toSSORegist() {
    const referer = encodeURIComponent(window.location.href);
    const registUrl = `${ssoURL}regist?referer=${referer}`;
    window.location.href = registUrl;
  }

  static toPersonalCenter() {
    const personalCenterUrl = `${siteURL}v/#/member/personalCenter/personalInfo`;
    window.location.href = personalCenterUrl;
  }

  /**
   * 跳转到企业培训页面
   */
  static toEnterprisePage() {
    window.location.href = `${siteURL}v/#/enterprise/enterpriseManage/enterpriseManage`;
  }

  /**
   * 跳转到大数据看板
   */
  static async toDashBoard() {
    try {
      const token = TokenManager.getToken();
      if (!token) {
        await this.toSSOLogin();
        return;
      }
      await EnterprisePermissionManager.checkEnterprisePermission();
      window.location.href = `${siteURL}v/#/dashboard/operation`;
    } catch (error) {
      console.error('跳转大数据看板失败:', error);
      await this.toSSOLogin();
    }
  }

  /**
   * 跳转到企业资源中心
   */
  static async toEnterpriseCenter() {
    try {
      const token = TokenManager.getToken();
      if (!token) {
        await this.toSSOLogin();
        return;
      }

      const userInfo = UserStorageManager.getUserInfo();
      if (userInfo && userInfo.enterpriseID) {
        window.location.href = `${siteURL}html/enterpriseCourseIndex.html`;
      } else {
        ElMessageBox.confirm('您不是企业人员，自动为您跳转到个人中心', '提示', {
          confirmButtonText: '确定',
          showCancelButton: false,
          showClose: false,
          closeOnClickModal: false,
          type: 'warning',
        }).then(() => {
          this.toPersonalCenter();
        });
      }
    } catch (error) {
      console.error('跳转企业资源中心失败:', error);
    }
  }
}

class SSOKeepAliveManager {
  /**
   * 启动保活机制
   */
  static startKeepAlive() {
    setInterval(
      () => {
        const token = TokenManager.getToken();
        if (token) {
          this._sendSSOKeepAlive();
        }
      },
      15 * 60 * 1000,
    );
  }

  /**
   * 发送SSO保活请求
   */
  static _sendSSOKeepAlive() {
    const el = document.createElement('img');
    el.width = 0;
    el.height = 0;
    document.body.appendChild(el);
    this._keepOnline(el, `${ssoURL}online?time=${Date.now()}`);
  }

  /**
   * 保持在线的通用方法
   * @private
   */
  static _keepOnline(el, src) {
    if (el) {
      el.src = src;
      setTimeout(() => {
        this._cleanupElement(el);
      }, CONFIG.IMG_CLEANUP_DELAY);
    }
  }

  /**
   * 清理DOM元素
   * @private
   */
  static _cleanupElement(element) {
    try {
      if (element && element.parentNode) {
        if (BrowserDetector.isIE) {
          element.removeNode(true);
        } else {
          element.remove();
        }
      }
    } catch (error) {
      console.error('清理DOM元素失败:', error);
    }
  }
}

class BrowserDetector {
  static userAgent = navigator.userAgent;
  static isIE = false;

  static checkIE() {
    this.isIE = this._detectIE();
  }

  static _detectIE() {
    const ua = this.userAgent;
    // IE11以下版本包含MSIE
    if (ua.indexOf('MSIE') > -1) {
      return true;
    }
    // IE11版本检测
    if (ua.indexOf('Trident') > -1 && ua.indexOf('rv') > -1) {
      return true;
    }

    return false;
  }

  /**
   * 获取浏览器详细信息
   */
  static getBrowserInfo() {
    const ua = this.userAgent;

    const patterns = {
      qqbrowser: /QQBrowser\/[\d.]+/gi,
      edge: /Edge\/[\d.]+/gi,
      ie: /Msie [\d.]+;/gi,
      firefox: /Firefox\/[\d.]+/gi,
      chrome: /Chrome\/[\d.]+/gi,
      safari: /Safari\/[\d.]+/gi,
    };

    if (ua.indexOf('QQBrowser') > -1) {
      return ua.match(patterns.qqbrowser)?.[0] || 'QQBrowser';
    }

    if (ua.indexOf('Edge') > -1) {
      return ua.match(patterns.edge)?.[0] || 'Edge';
    }

    if (ua.indexOf('MSIE') > -1) {
      this.isIE = true;
      return ua.match(patterns.ie)?.[0] || 'IE';
    }

    if (ua.indexOf('Trident') > -1 && ua.indexOf('rv') > -1) {
      const version = ua.match(/rv:(\d+\.\d+)/);
      return version ? `IE/${version[1]}` : 'IE/11';
    }

    if (ua.indexOf('Firefox') > -1) {
      return ua.match(patterns.firefox)?.[0] || 'Firefox';
    }

    if (ua.indexOf('Chrome') > -1) {
      return ua.match(patterns.chrome)?.[0] || 'Chrome';
    }

    if (ua.indexOf('Safari') > -1 && ua.indexOf('Chrome') === -1) {
      return ua.match(patterns.safari)?.[0] || 'Safari';
    }

    return null;
  }
}

class EnterprisePermissionManager {
  /**
  /**
   * 检查企业权限
   * {
   *   enterpriseMember: 0|1,  // 是否为企业成员
   *   enterpriseManager: 0|1  // 是否为企业管理员
   * }
   */
  static async checkEnterprisePermission() {
    try {
      const userInfo = UserStorageManager.getUserInfo();
      const userType = userInfo?.userType ? parseInt(userInfo.userType) : 0;

      let result = {
        enterpriseMember: 0,
        enterpriseManager: 0,
      };

      if (userInfo && userInfo.enterpriseID) {
        result.enterpriseMember = 1;
      }

      if (userType !== 1 && userType !== 99) {
        const token = TokenManager.getToken();
        if (!token) {
          return result;
        }

        const response = await request.get(
          `${courseApis}member/personalcenter/checkmemberroletype`,
        );

        if (!response.status) {
          return result;
        }

        if (response.data.isManager === 'Y') {
          if (response.data.roleType && parseInt(response.data.roleType)) {
            // 更新用户信息
            const updatedUserInfo = {
              ...userInfo,
              enterpriseID: response.data.enterpriseID,
              enterpriseInnercode: response.data.enterpriseInnercode,
              enterpriseName: response.data.enterpriseName,
              userType: response.data.roleType,
            };

            UserStorageManager.setUserInfo(JSON.stringify(updatedUserInfo));

            result.enterpriseManager = 1;
            result.enterpriseMember = 1;

            // 同步企业权限到 Pinia store
            try {
              const userStore = useUserStore();
              userStore.setEnterprisePermission({
                enterpriseMember: result.enterpriseMember as 0 | 1,
                enterpriseManager: result.enterpriseManager as 0 | 1,
              });
            } catch (error) {
              console.error('同步企业权限到 Pinia store 失败:', error);
            }
          }
        }
      } else {
        result.enterpriseManager = 1;
        result.enterpriseMember = 1;
      }

      return result;
    } catch (error) {
      console.error('检查企业权限失败:', error);
      return {
        enterpriseMember: 0,
        enterpriseManager: 0,
      };
    }
  }
}

class PageInitManager {
  static initData() {
    try {
      const userInfo = UserStorageManager.getUserInfo();
      const token = TokenManager.getToken();
      LogManager.postLog(
        LogAPIURL,
        BigDataCode,
        userInfo?.ID || null,
        userInfo?.userName || null,
        userInfo?.enterpriseID || '',
        userInfo?.enterpriseInnercode || '',
        '',
        '',
      );
      if (token && userInfo) {
        this._handlePersonalInfoReminder(userInfo);
      }
    } catch (error) {
      console.error('页面初始化失败:', error);
    }
  }

  /**
   * 处理完善个人信息提示
   * @private
   */
  static async _handlePersonalInfoReminder(userInfo) {
    try {
      const userStore = useUserStore();
      if (userInfo.consummate) {
        userStore.isLogin = true;
        try {
          await ElMessageBox.confirm('您的个人信息暂不完善，请前往个人中心完善信息！', '提示', {
            confirmButtonText: '确定',
            showCancelButton: false,
            showClose: false,
            closeOnClickModal: false,
            type: 'warning',
          });
          window.location.href =
            siteURL + 'v/#/member/personalCenter/personalInfo?activeName=modifyInfo';
        } catch (error) {
          console.log(error);
          return;
        }
      }

      if (userInfo.AccessName == userInfo.userName) {
        userStore.isLogin = true;
        return;
      }

      userStore.isLogin = false;
    } catch (error) {
      console.error('处理个人信息提醒失败:', error);
    }
  }
}

/**
 * Token刷新管理器
 * 处理token自动刷新逻辑
 */
class TokenRefreshManager {
  /**
   * 刷新token
   */
  static async refreshToken() {
    try {
      // 首先检查SSO登录状态
      const ssoResponse = await request.get(`${ssoURL}online?time=${Date.now()}`);

      if (!ssoResponse.Login) {
        console.log('SSO未登录，无法刷新token');
        return;
      }

      const token = TokenManager.getToken();
      if (!token) {
        console.log('本地token为空，请求SSO登录');
        await SSOAuthManager._requestSSOLogin();
        return;
      }

      // 刷新token
      const refreshResponse = await request.get(`${courseApis}token/refresh/web`);

      if (refreshResponse.status === 1) {
        TokenManager.setToken(refreshResponse.data.token);
        console.log('Token刷新成功');
      } else {
        console.log('Token刷新失败:', refreshResponse.message);
      }
    } catch (error) {
      console.error('刷新token失败:', error);
    }
  }

  /**
   * 启动自动刷新
   */
  static startAutoRefresh() {
    setInterval(
      () => {
        this.refreshToken();
      },
      15 * 60 * 1000,
    ); // 每15分钟刷新一次
  }
}

/**
 * 日志管理器
 * 用于推送日志数据到大数据平台
 */
class LogManager {
  /**
   * 推送日志数据到大数据平台
   * @param url 日志接口地址
   * @param code 日志代码
   * @param userId 用户ID
   * @param userName 用户名
   * @param enterpriseId 企业ID
   * @param enterpriseInnercode 企业内部编码
   * @param contentID 内容ID
   * @param contentTitle 内容标题
   */
  public static async postLog(
    url: string,
    code: string,
    userId: string,
    userName: string,
    enterpriseId: string,
    enterpriseInnercode: string,
    contentID: string,
    contentTitle: string,
  ): Promise<void> {
    try {
      const urlFullPath = window.location.href;
      const visitDate = Utils.getNowDate();
      const platform = Utils.isMobileBrowser() ? 'h5' : 'pc';
      const courseId = getParams('courseID');
      const resourceId =getParams('courseID') ? '': getParams('liveCourseID') || getParams('specialID');
      const logData = {
        name: code,
        urlFullPath,
        visitDate,
        userId,
        userName,
        courseId: Number(courseId), // 课程ID\ 课程播放页面
        resourceId:Number(resourceId), // 直播ID 和专题 ID 都是这个字段
        enterpriseId,
        enterpriseInnercode,
        contentId: contentID,
        contentTitle,
        platform,
      };
      await request.post(url, stringify(logData));
    } catch (error) {
      console.error('日志推送失败:', error);
    }
  }

  /**
   * 推送日志数据到大数据平台（同步版本，不等待响应）
   * @param url 日志接口地址
   * @param code 日志代码
   * @param userId 用户ID
   * @param userName 用户名
   * @param enterpriseId 企业ID
   * @param enterpriseInnercode 企业内部编码
   * @param contentID 内容ID
   * @param contentTitle 内容标题
   */
  public static postLogSync(
    url: string,
    code: string,
    userId: string,
    userName: string,
    enterpriseId: string,
    enterpriseInnercode: string,
    contentID: string,
    contentTitle: string,
  ): void {
    // 异步执行，不等待结果
    this.postLog(
      url,
      code,
      userId,
      userName,
      enterpriseId,
      enterpriseInnercode,
      contentID,
      contentTitle,
    ).catch((error) => {
      console.error('异步日志推送失败:', error);
    });
  }

  /**
   * 批量推送日志
   * @param logs 日志数据数组
   */
  public static async postLogsBatch(
    logs: Array<{
      url: string;
      code: string;
      userId: string;
      userName: string;
      enterpriseId: string;
      enterpriseInnercode: string;
      contentID: string;
      contentTitle: string;
    }>,
  ): Promise<void> {
    try {
      const promises = logs.map((log) =>
        this.postLog(
          log.url,
          log.code,
          log.userId,
          log.userName,
          log.enterpriseId,
          log.enterpriseInnercode,
          log.contentID,
          log.contentTitle,
        ),
      );

      await Promise.all(promises);
    } catch (error) {
      console.error('批量日志推送失败:', error);
      throw error;
    }
  }
}

class Utils {
  static isEmpty(value) {
    return value === '' || value == null || value === undefined || value === 'undefined';
  }
  static isEmptyObject(obj) {
    if (!obj || typeof obj !== 'object') {
      return true;
    }

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        return false;
      }
    }
    return true;
  }

  static getNowDate(): string {
    const now = new Date();
    return now.toISOString().slice(0, 19).replace('T', ' ');
  }

  static isMobileBrowser(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
    return mobileRegex.test(userAgent);
  }
}

const initUserState = () => {
  try {
    const userInfo = UserStorageManager.getUserInfo();
    const token = TokenManager.getToken();

    if (userInfo && userInfo.userName && token) {
      const userStore = useUserStore();
      userStore.handleLoginSuccess(userInfo, token);
      console.log('用户状态已同步到 Pinia store');
    }
  } catch (error) {
    console.error('初始化用户状态失败:', error);
  }
};

const init = async () => {
  BrowserDetector.checkIE();
  SSOKeepAliveManager.startKeepAlive();
  TokenRefreshManager.startAutoRefresh();
  PageInitManager.initData();
  initUserState();
};

const getUserInfo = () => UserStorageManager.getUserInfo();
const setUserInfo = (userinfo) => UserStorageManager.setUserInfo(userinfo);
const removeUserInfo = () => UserStorageManager.removeUserInfo();
const getToken = () => TokenManager.getToken();
const setToken = (token) => TokenManager.setToken(token);
const removeToken = () => TokenManager.removeToken();
const checkLogin = async () => await SSOAuthManager.checkLogin();
const toSSOLogin = async (referer?: string) => await SSONavigationManager.toSSOLogin(referer);
const toSSOLogout = () => SSONavigationManager.toSSOLogout();
const toSSORegist = () => SSONavigationManager.toSSORegist();
const toPersonalCenter = () => SSONavigationManager.toPersonalCenter();
const toEnterprisePage = async () => await SSONavigationManager.toEnterprisePage();
const toDashBoard = async () => await SSONavigationManager.toDashBoard();
const toEnterpriseCenter = async () => await SSONavigationManager.toEnterpriseCenter();
const checkEnterprisePermission = async () =>
  await EnterprisePermissionManager.checkEnterprisePermission();

const initData = () => PageInitManager.initData();
const refreshToken = async () => await TokenRefreshManager.refreshToken();
const isEmpty = (value) => Utils.isEmpty(value);
const isEmptyObject = (obj) => Utils.isEmptyObject(obj);
const getBrowserInfo = () => BrowserDetector.getBrowserInfo();

export {
  getUserInfo,
  setUserInfo,
  removeUserInfo,
  getToken,
  setToken,
  removeToken,
  checkLogin,
  toSSOLogin,
  toSSOLogout,
  toSSORegist,
  toPersonalCenter,
  toEnterprisePage,
  toDashBoard,
  toEnterpriseCenter,
  checkEnterprisePermission,
  initData,
  refreshToken,
  isEmpty,
  isEmptyObject,
  getBrowserInfo,
  CONFIG,
  init,
  initUserState,
  useUserStore,
};
